import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, SideDrawer, useUnsavedChangesGuard } from '@klareospace/pharos-lib-web-utils';
import { BUILT_IN_ROLE_KEY, ROLE_SCOPE } from '@klareospace/pharos-lib-types';
import type { UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import { usePermissions } from '@klareospace/pharos-lib-front-utils';
import {
  deduplicate,
  getParentSpaceIdsFromResolvedScopes,
  getScopeIdsByLevel,
  getSpaceLabelsFromResolvedScopes,
} from '@klareospace/pharos-lib-utils';

import { useAuth } from '&/auth';
import { useMasterContext } from '&/master';
import { useSpacesForSelect } from '../../spaces/hooks';
import { useDriverFields } from '../hooks/use-driver-fields';
import { useUserForm } from '../hooks/use-user-form';
import type { UseUserFormOptions } from '../hooks/use-user-form';
import { useRoleOptions } from '../hooks/use-role-options';
import { useSpaceSiteSelection } from '../hooks/use-space-site-selection';
import { UserFormFields } from './user-form-fields';
import { UserStatusHeaderAction } from './user-status-header-action';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UserDrawerEntryPoint = 'drivers' | 'users';

export interface UserDrawerProps {
  /** Which management page opened the drawer. Fields, validation, and endpoints are identical
   * across entry points. The drivers entry point pre-selects the DRIVER role. In CREATE mode the
   * roles dropdown stays editable with the DRIVER chip locked (non-removable); in EDIT mode the
   * roles field is fully disabled — role management for existing users happens exclusively from
   * the users entry point, where roles stay fully editable. Only the title/labels,
   * pre-selected/locked scope values, and this roles-field behavior differ. */
  entryPoint?: UserDrawerEntryPoint;
  /** Create-mode-only scope locks (drivers entry point), mirroring the former DriverDrawer. */
  lockedCarrierId?: string;
  lockedSiteId?: string;
  lockedSpaceId?: string;
  onClose: () => void;
  open: boolean;
  /**
   * null  → create mode (invite new user)
   * User  → edit mode (edit existing user)
   */
  user: UserWithResolvedScopes | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface DrawerTexts {
  cancel: string;
  submit: string;
  subtitle: string;
  title: string;
}

interface DrawerTextKeys {
  cancelKey: string;
  submitKey: string;
  subtitleKey: string;
  titleKey: string;
}

// Returns i18n KEYS only (never calls t()) so the caller's own useTranslation('settings') scope
// resolves them — keeps the i18next-no-undefined-translation-keys lint rule namespace-aware.
function getDrawerTextKeys(isDriverEntry: boolean, isEditMode: boolean): DrawerTextKeys {
  if (isDriverEntry) {
    return {
      cancelKey: 'drivers.drawer.cancel',
      submitKey: isEditMode ? 'drivers.drawer.submitEdit' : 'drivers.drawer.submit',
      subtitleKey: isEditMode ? 'drivers.drawer.editSubtitle' : 'drivers.drawer.createSubtitle',
      titleKey: isEditMode ? 'drivers.drawer.editTitle' : 'drivers.drawer.createTitle',
    };
  }
  return {
    cancelKey: isEditMode ? 'users.edit.cancel' : 'users.inviteDrawer.cancel',
    submitKey: isEditMode ? 'users.edit.submit' : 'users.inviteDrawer.submit',
    subtitleKey: isEditMode ? 'users.edit.subtitle' : 'users.inviteDrawer.subtitle',
    titleKey: isEditMode ? 'users.edit.title' : 'users.inviteDrawer.title',
  };
}

interface UnsavedChangesLabelKeys {
  cancelKey: string;
  confirmKey: string;
  descriptionKey: string;
  titleKey: string;
}

function getUnsavedChangesLabelKeys(isDriverEntry: boolean, isEditMode: boolean): UnsavedChangesLabelKeys {
  if (isDriverEntry) {
    return {
      cancelKey: 'drivers.drawer.unsavedChanges.cancel',
      confirmKey: 'drivers.drawer.unsavedChanges.confirm',
      descriptionKey: 'drivers.drawer.unsavedChanges.description',
      titleKey: 'drivers.drawer.unsavedChanges.title',
    };
  }
  return {
    cancelKey: isEditMode ? 'users.edit.unsavedChanges.cancel' : 'users.inviteDrawer.unsavedChanges.cancel',
    confirmKey: isEditMode ? 'users.edit.unsavedChanges.confirm' : 'users.inviteDrawer.unsavedChanges.confirm',
    descriptionKey: isEditMode
      ? 'users.edit.unsavedChanges.description'
      : 'users.inviteDrawer.unsavedChanges.description',
    titleKey: isEditMode ? 'users.edit.unsavedChanges.title' : 'users.inviteDrawer.unsavedChanges.title',
  };
}

function computeIsSubmitDisabled(isEditMode: boolean, isDirty: boolean, isPending: boolean, isValid: boolean): boolean {
  return isEditMode ? isPending || !isValid || !isDirty : isPending;
}

function getHeaderAction(
  isEditMode: boolean,
  onStatusChange: () => void,
  user: UserWithResolvedScopes | null,
): React.JSX.Element | undefined {
  if (!isEditMode || !user) return undefined;
  return <UserStatusHeaderAction onStatusChange={onStatusChange} user={user} />;
}

interface ScopeLockProps {
  isSiteLocked: boolean;
  isSpaceLocked: boolean;
  lockedSiteIdForFields: string | null;
}

interface ComputeScopeLockParams {
  isDriverEntry: boolean;
  isEditMode: boolean;
  lockedCarrierId: string | undefined;
  lockedSiteId: string | undefined;
  lockedSpaceId: string | undefined;
  masterSiteId: string | null;
  masterSpaceId: string | null;
}

// Drivers entry point locks are create-mode only (edit mode always shows the driver's real scope,
// unlocked). The users entry point keeps its existing master-context-based lock, unchanged.
function computeScopeLockProps({
  isDriverEntry,
  isEditMode,
  lockedCarrierId,
  lockedSiteId,
  lockedSpaceId,
  masterSiteId,
  masterSpaceId,
}: ComputeScopeLockParams): ScopeLockProps {
  if (isDriverEntry) {
    return {
      isSiteLocked: false,
      isSpaceLocked: !isEditMode && (lockedSpaceId !== undefined || lockedCarrierId !== undefined),
      lockedSiteIdForFields: isEditMode ? null : (lockedSiteId ?? null),
    };
  }
  return {
    isSiteLocked: !!masterSiteId,
    isSpaceLocked: !!masterSpaceId,
    lockedSiteIdForFields: isEditMode ? null : masterSiteId,
  };
}

interface GetFormOptionsParams {
  isDriverEntry: boolean;
  isEditMode: boolean;
  lockedCarrierId: string | undefined;
  lockedSiteId: string | undefined;
  lockedSpaceId: string | undefined;
  masterSiteId: string | null;
  masterSpaceId: string | null;
  open: boolean;
}

// Only relevant for create mode (edit mode always derives its defaults from the edited user,
// never from these locked/master-context values). `open` lets useUserForm re-seed create-mode
// defaults from the CURRENT locked props on every closed→open transition — UserDrawer is
// permanently mounted by its callers, so without this the reset effect (keyed on user id) never
// re-fires for create mode. The users entry point mirrors the drivers entry point's locked-scope
// behavior whenever the page pins a single site (e.g. a site's Users tab): without this, the
// invite silently falls back to a space-wide scope (see the site-selection-map seed effect below
// for how the submitted scope is then narrowed back down to that site).
function getFormOptions({
  isDriverEntry,
  isEditMode,
  lockedCarrierId,
  lockedSiteId,
  lockedSpaceId,
  masterSiteId,
  masterSpaceId,
  open,
}: GetFormOptionsParams): UseUserFormOptions | undefined {
  if (isEditMode) return undefined;
  if (isDriverEntry) {
    return {
      initialCarrierId: lockedCarrierId,
      initialRoles: [BUILT_IN_ROLE_KEY.DRIVER],
      initialSiteId: lockedSiteId,
      initialSpaceId: lockedSpaceId,
      open,
    };
  }
  if (!masterSiteId) return undefined;
  return {
    initialSiteId: masterSiteId,
    initialSpaceId: masterSpaceId ?? undefined,
    open,
  };
}

// The space that owns a create-mode drawer's locked/pinned site: the `lockedSpaceId` prop for the
// drivers entry point, the master-context space for the users entry point. Used to backfill the
// site-selection map so the pinned site submits as a site scope (see the seed effect below).
function resolveLockedSpaceIdForSiteSeed(
  isDriverEntry: boolean,
  lockedSpaceId: string | undefined,
  masterSpaceId: string | null,
): string | undefined {
  return isDriverEntry ? lockedSpaceId : (masterSpaceId ?? undefined);
}

interface SeedLockedSiteSelectionParams {
  isEditMode: boolean;
  lockedSiteIdForFields: string | null;
  lockedSpaceIdForSiteSeed: string | undefined;
  open: boolean;
  seedSiteSelection: (spaceId: string, siteIds: string[]) => void;
}

function seedLockedSiteSelection({
  isEditMode,
  lockedSiteIdForFields,
  lockedSpaceIdForSiteSeed,
  open,
  seedSiteSelection,
}: SeedLockedSiteSelectionParams): void {
  if (!open || isEditMode || !lockedSiteIdForFields || !lockedSpaceIdForSiteSeed) return;
  seedSiteSelection(lockedSpaceIdForSiteSeed, [lockedSiteIdForFields]);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface UserDrawerFooterProps {
  attemptClose: () => void;
  isSubmitDisabled: boolean;
  onSubmit: () => void;
  texts: DrawerTexts;
}

function UserDrawerFooter({
  attemptClose,
  isSubmitDisabled,
  onSubmit,
  texts,
}: UserDrawerFooterProps): React.JSX.Element {
  return (
    <>
      <Button disabled={isSubmitDisabled} onClick={onSubmit} type="button">
        {texts.submit}
      </Button>
      <Button onClick={attemptClose} type="button" variant="outline">
        {texts.cancel}
      </Button>
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function UserDrawer({
  entryPoint = 'users',
  lockedCarrierId,
  lockedSiteId,
  lockedSpaceId,
  onClose,
  open,
  user,
}: UserDrawerProps): React.JSX.Element {
  const { t } = useTranslation('settings');
  const isEditMode = user !== null;
  const isDriverEntry = entryPoint === 'drivers';

  const { sendPasswordResetEmail } = useAuth();
  const { siteId: masterSiteId, spaceId: masterSpaceId } = useMasterContext();
  const { hasPermission } = usePermissions();

  const { isSiteLocked, isSpaceLocked, lockedSiteIdForFields } = computeScopeLockProps({
    isDriverEntry,
    isEditMode,
    lockedCarrierId,
    lockedSiteId,
    lockedSpaceId,
    masterSiteId,
    masterSpaceId,
  });

  // ── Scope derivation (edit mode) ──────────────────────────────────────────
  const userSpaceIds = useMemo(() => (user?.scopes ? getScopeIdsByLevel(user.scopes, ROLE_SCOPE.SPACE) : []), [user]);
  const derivedSpaceIds = useMemo(
    (): string[] => getParentSpaceIdsFromResolvedScopes(user?.resolvedScopes ?? []),
    [user],
  );
  const mergedSpaceIds = useMemo(
    () => deduplicate([...userSpaceIds, ...derivedSpaceIds]),
    [userSpaceIds, derivedSpaceIds],
  );

  // ── Default space IDs (create mode, users entry point) ────────────────────
  const defaultSpaceIds = useMemo(() => (masterSpaceId ? [masterSpaceId] : []), [masterSpaceId]);

  const spaceOptions = useSpacesForSelect({ enabled: open && hasPermission('spaces:list') });
  // Fallback labels from the user's own resolvedScopes cover callers without `spaces:list`
  // (e.g. SITE_MANAGER); spaceOptions (fetched via the permission-gated list) take precedence.
  const spaceLabelMap = useMemo(() => {
    const labels = getSpaceLabelsFromResolvedScopes(user?.resolvedScopes ?? []);
    for (const option of spaceOptions) labels.set(option.value, option.label);
    return labels;
  }, [spaceOptions, user]);
  const roleOptions = useRoleOptions();

  function handleSuccess(): void {
    form.reset();
    onClose();
  }

  const formOptions = getFormOptions({
    isDriverEntry,
    isEditMode,
    lockedCarrierId,
    lockedSiteId,
    lockedSpaceId,
    masterSiteId,
    masterSpaceId,
    open,
  });
  const { form, isPending, onSubmit } = useUserForm(user, handleSuccess, formOptions);
  const { handleSiteSelectionChange, seedSiteSelection, spaceSiteSelectionRef } = useSpaceSiteSelection(form);

  // ── Unsaved changes guard — keys differ by mode/entry point ───────────────
  const unsavedChangesLabelKeys = getUnsavedChangesLabelKeys(isDriverEntry, isEditMode);
  const { attemptClose, handleOpenChange, unsavedChangesDialog } = useUnsavedChangesGuard(form, {
    labels: {
      cancel: t(unsavedChangesLabelKeys.cancelKey),
      confirm: t(unsavedChangesLabelKeys.confirmKey),
      description: t(unsavedChangesLabelKeys.descriptionKey),
      title: t(unsavedChangesLabelKeys.titleKey),
    },
    onClose,
  });

  // ── Seed space IDs on open ─────────────────────────────────────────────────
  useEffect((): void => {
    if (!open) return;
    if (isEditMode && mergedSpaceIds.length > 0) {
      form.setValue('spaceIds', mergedSpaceIds);
      return;
    }
    if (!isEditMode && !isDriverEntry && defaultSpaceIds.length > 0) {
      form.setValue('spaceIds', defaultSpaceIds);
    }
  }, [open, isEditMode, isDriverEntry, mergedSpaceIds, defaultSpaceIds, form]);

  // ── Seed the site-selection map for a locked/pinned site (create mode only) ──
  // buildCreateParams only narrows a space's scope down to its site(s) when the submit-time
  // space→site map (spaceSiteSelectionRef) has an entry for that space — normally populated by
  // user interaction with the site selector. A create-mode drawer pre-pinned to a single site
  // (drivers entry via lockedSiteId, users entry via masterSiteId) renders that selector
  // disabled, so it never fires — backfill the map here or the pinned site submits as a
  // space-wide scope instead of a site scope.
  const lockedSpaceIdForSiteSeed = resolveLockedSpaceIdForSiteSeed(isDriverEntry, lockedSpaceId, masterSpaceId);
  useEffect((): void => {
    seedLockedSiteSelection({ isEditMode, lockedSiteIdForFields, lockedSpaceIdForSiteSeed, open, seedSiteSelection });
  }, [open, isEditMode, lockedSiteIdForFields, lockedSpaceIdForSiteSeed, seedSiteSelection]);

  const selectedSpaceIds: string[] = form.watch('spaceIds') ?? [];
  const formRoles: string[] = form.watch('roles') ?? [];
  const isDriverRole = formRoles.includes(BUILT_IN_ROLE_KEY.DRIVER);
  const isTenantAdmin = formRoles.includes(BUILT_IN_ROLE_KEY.TENANT_ADMIN);

  // ── Driver section ────────────────────────────────────────────────────────
  const { carrierOptions, driverVehicleField, siteFilterForSpace } = useDriverFields({
    form,
    hasPermission,
    isDriverRole,
    isTenantAdmin,
    lockedCarrierId,
    lockedSiteId,
    lockedSpaceId,
    open,
    user,
  });

  // ── Password reset (edit mode only) ───────────────────────────────────────
  function handleSendPasswordReset(): void {
    if (user?.email) {
      void sendPasswordResetEmail(user.email);
      toast.success(t('users.edit.sendPasswordResetSuccess'));
    }
  }

  const isSubmitDisabled = computeIsSubmitDisabled(
    isEditMode,
    form.formState.isDirty,
    isPending,
    form.formState.isValid,
  );
  const headerAction = getHeaderAction(isEditMode, attemptClose, user);
  const drawerTextKeys = getDrawerTextKeys(isDriverEntry, isEditMode);
  const texts: DrawerTexts = {
    cancel: t(drawerTextKeys.cancelKey),
    submit: t(drawerTextKeys.submitKey),
    subtitle: t(drawerTextKeys.subtitleKey),
    title: t(drawerTextKeys.titleKey),
  };

  return (
    <>
      <SideDrawer
        {...(isEditMode ? { autoFocus: false } : {})}
        dismissOnOutsideClick={false}
        footer={
          <UserDrawerFooter
            attemptClose={attemptClose}
            isSubmitDisabled={isSubmitDisabled}
            onSubmit={form.handleSubmit((data): void => onSubmit(data, spaceSiteSelectionRef.current))}
            texts={texts}
          />
        }
        headerAction={headerAction}
        onOpenChange={handleOpenChange}
        open={open}
        subtitle={texts.subtitle}
        title={texts.title}
      >
        <UserFormFields
          carrierOptions={carrierOptions}
          driverVehicleField={driverVehicleField}
          form={form}
          handleSiteSelectionChange={handleSiteSelectionChange}
          isDriverRole={isDriverRole}
          isSiteLocked={isSiteLocked}
          isSpaceLocked={isSpaceLocked}
          lockedSiteId={lockedSiteIdForFields}
          onSendPasswordReset={handleSendPasswordReset}
          roleOptions={roleOptions}
          rolesDisabled={isDriverEntry && isEditMode}
          rolesLockedValues={isDriverEntry && !isEditMode ? [BUILT_IN_ROLE_KEY.DRIVER] : undefined}
          selectedSpaceIds={selectedSpaceIds}
          siteFilterForSpace={siteFilterForSpace}
          spaceLabelMap={spaceLabelMap}
          spaceOptions={spaceOptions}
          user={user}
        />
      </SideDrawer>
      {unsavedChangesDialog}
    </>
  );
}
